import type { ColumnState, GridApi } from 'ag-grid-community';

/**
 * [MCV-1405] Persistence for a grid's column arrangement — order and sort — so
 * a user's re-sort survives navigating away from the page.
 *
 * Stored in localStorage (per-device, not per-account) keyed on the grid's
 * `cyTag`, which is already required and unique per grid.
 *
 * Deliberately excludes width/flex. `defaultColDef` sets `flex: 1`, so columns
 * size themselves to the container; replaying pixel widths captured at one
 * viewport would fight that at every other viewport.
 */

const STORAGE_PREFIX = 'mcv.grid.columnState';

// Bumped when the stored shape changes. A mismatch is discarded rather than
// migrated — the cost of losing a column arrangement is trivial next to the
// cost of restoring a malformed one.
//
// [MCV-1405] 1 -> 2: before this change the Actions column had no explicit
// colId, so ag-grid fell back to an index-derived one (e.g. "0") and the
// column was draggable out of its pinned slot. A v1 payload can therefore
// carry a positional, auto-generated colId standing in for Actions and/or a
// stale slot recording it mid-list from before it was locked in place.
// `lockPosition` alone would correct the on-screen order but not scrub that
// bogus stored record, so the version bump discards every v1 payload via the
// mismatch path below rather than attempting to migrate it.
//
// [MCV-1753] 2 -> 3: Certification, Issuer, Active (certifications) and Coverage Period, Status
// (malpractice insurance) and Claim Status (malpractice claims) had no colId and no field, so a v2
// payload for those grids holds generated ids ("0", "1", "2"). Those ids now match no column; they
// are filtered out and applyOrder appends the real columns after the date columns, which restores
// the scrambled order the colIds exist to prevent. Discard rather than migrate, as above.
const SCHEMA_VERSION = 3;

type PersistedColumn = Pick<ColumnState, 'colId' | 'sort' | 'sortIndex'>;

type StoredColumnState = {
  version: number;
  state: PersistedColumn[];
};

const storageKey = (gridId: string) => `${STORAGE_PREFIX}.${gridId}`;

// localStorage throws in private-browsing/quota cases and is absent in SSR and
// some test environments. A grid that can't remember its layout is a far better
// outcome than a grid that won't render, so every access is best-effort.
const safeRead = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeWrite = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* quota exceeded or storage unavailable — layout just won't persist */
  }
};

const safeRemove = (key: string): void => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* nothing to do */
  }
};

/**
 * localStorage is untrusted input: it survives across releases and can be
 * hand-edited. A `null` entry would satisfy the array check above and then
 * throw on property access, so every entry is validated before use rather
 * than asserted into shape.
 */
const isPersistedColumn = (column: unknown): column is PersistedColumn => {
  if (!column || typeof column !== 'object') return false;
  const { colId, sort, sortIndex } = column as Record<string, unknown>;
  return (
    typeof colId === 'string' &&
    colId.length > 0 &&
    (sort === 'asc' || sort === 'desc' || sort === null || sort === undefined) &&
    (sortIndex === null ||
      sortIndex === undefined ||
      (typeof sortIndex === 'number' && Number.isInteger(sortIndex) && sortIndex >= 0))
  );
};

/** The slice of an ag-Grid column event this policy inspects. */
export type ColumnPersistEvent = { finished?: boolean; source?: string };

/**
 * Whether an `onColumnMoved`/`onSortChanged` event should persist the current
 * arrangement.
 *
 * A column drag emits a stream of `onColumnMoved` events and only the last
 * carries `finished: true`; the intermediate ones are skipped so a drag does
 * not hammer storage with positions the user is still dragging through.
 * `onSortChanged` has no `finished` flag, so its absence counts as done.
 *
 * ag-Grid raises these same events for programmatic causes too —
 * `gridOptionsChanged` when a new `columnDefs` array arrives, plus `api`,
 * `flex`, `rowDataUpdated`. Persisting on those would stamp whatever the grid
 * just reset itself to over the user's saved arrangement, so only user-driven
 * sources are honoured. Every one is prefixed `ui` (uiColumnMoved,
 * uiColumnDragged, uiColumnSorted).
 *
 * Extracted from the grid so the guard is unit-testable directly: a browser
 * rerender test cannot observe it, because `maintainColumnOrder` leaves the
 * grid in the same order and a stray write would persist an identical payload.
 */
export const shouldPersistColumnEvent = ({ finished, source }: ColumnPersistEvent): boolean => {
  if (finished === false) return false;
  return source?.startsWith('ui') ?? false;
};

export const saveColumnState = (gridId: string, api: GridApi): void => {
  const state: PersistedColumn[] = api
    .getColumnState()
    .map(({ colId, sort, sortIndex }) => ({ colId, sort, sortIndex }));
  const payload: StoredColumnState = { version: SCHEMA_VERSION, state };
  safeWrite(storageKey(gridId), JSON.stringify(payload));
};

/**
 * Restores a previously saved arrangement onto `api`.
 *
 * Entries whose `colId` is no longer present in the grid are dropped. Without
 * that filter, renaming or removing a column in a future colDef change would
 * leave users pinned to a stale saved state they have no way to clear from the
 * UI. Anything unparseable or from a different schema version is discarded.
 */
export const restoreColumnState = (gridId: string, api: GridApi): void => {
  const key = storageKey(gridId);
  const raw = safeRead(key);
  if (!raw) return;

  let parsed: StoredColumnState;
  try {
    parsed = JSON.parse(raw) as StoredColumnState;
  } catch {
    safeRemove(key);
    return;
  }

  if (parsed?.version !== SCHEMA_VERSION || !Array.isArray(parsed.state)) {
    safeRemove(key);
    return;
  }

  const liveColIds = new Set(api.getColumnState().map((column) => column.colId));
  const state = parsed.state
    .filter(isPersistedColumn)
    .filter(({ colId }) => liveColIds.has(colId))
    // Re-project rather than forward the parsed object. A hand-edited or
    // older payload can carry extra keys — width and flex especially — and
    // passing those through would defeat the exclusion this module exists to
    // enforce.
    .map(({ colId, sort, sortIndex }) => ({ colId, sort, sortIndex }));
  if (state.length === 0) {
    safeRemove(key);
    return;
  }

  // [MCV-1798] A save with no sorted column records only a column move, yet
  // replaying its `sort: null` entries would clear the colDef's `initialSort`
  // default. Apply the order alone then, so the default sort still holds.
  const userSorted = state.some(({ sort }) => sort != null);
  api.applyColumnState({
    state: userSorted ? state : state.map(({ colId }) => ({ colId })),
    applyOrder: true,
  });
};
